import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { useProjects, resolveImg } from "@/hooks/usePublicContent";

export default function Projects() {
  const { items } = useProjects();
  if (!items.length) return null; // approved-content-only: hide when empty
  const list = items.slice(0, 6);

  return (
    <section id="projects" data-testid="projects-section" className="relative bg-[#f8fafc] py-24 md:py-36">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <p className="overline !text-[#9c7a4d] mb-5">Recent Work</p>
            <h2 className="font-serif font-light text-[#0f172a] text-4xl sm:text-5xl lg:text-6xl leading-[1] tracking-tight">
              Recent Perth<br />tiling projects.
            </h2>
          </div>
          <p className="text-[#475569] text-lg font-light leading-relaxed max-w-md">
            A selection of recently completed work across Perth — from book-matched marble bathrooms to
            large-format porcelain floors and pool surrounds.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {list.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-6%" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}
            >
              <Link
                to={`/projects/${p.slug}`}
                data-testid={`project-card-${p.slug}`}
                className="group block border border-[#0f172a]/10 bg-white overflow-hidden hover:shadow-[0_24px_60px_-30px_rgba(33,29,24,0.4)] transition-shadow"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={resolveImg(p.image)}
                    alt={p.image_alt || `${p.title} — ${p.service} in ${p.suburb}, Perth`}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.1s] ease-out group-hover:scale-105"
                  />
                  {p.service ? (
                    <span className="absolute top-3 left-3 bg-[#0b1120]/85 text-white text-[0.65rem] uppercase tracking-[0.16em] px-3 py-1.5">
                      {p.service}
                    </span>
                  ) : null}
                </div>
                <div className="p-6">
                  {p.suburb ? (
                    <p className="overline !text-[#94a3b8] mb-2 flex items-center gap-1.5">
                      <MapPin className="h-3 w-3 text-[#9c7a4d]" /> {p.suburb}
                    </p>
                  ) : null}
                  <h3 className="font-serif text-2xl text-[#0f172a] font-light leading-tight mb-2">{p.title}</h3>
                  <p className="text-[#64748b] font-light text-sm leading-relaxed line-clamp-2">{p.summary}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#9c7a4d]">
                    View project <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
